// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ConsentBanner
 * @package @geenius/analytics/solidjs-css
 * @description Renders the SolidJS CSS consent banner used to capture
 * analytics cookie preferences. The component keeps the vanilla-CSS class
 * contract stable while updating provider-backed consent state.
 */

import type { ConsentSettings } from "@geenius/analytics";
import { Button, Card, CardContent } from "@geenius/ui/solidjs-css";
import { type Component, createSignal, Show } from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { FadeIn } from "../lib/FadeIn";
import { useAnalyticsConsent } from "../provider";

/**
 * Props for rendering the SolidJS CSS analytics consent banner.
 *
 * @property onAccept Called when the user grants analytics consent.
 * @property onReject Called when the user rejects analytics consent.
 * @property show Whether the banner should be visible on first render.
 * @property class Optional class name merged into the banner wrapper.
 */
export interface ConsentBannerProps {
  onAccept: (settings: ConsentSettings) => void;
  onReject: (settings: ConsentSettings) => void;
  show?: boolean;
  class?: string;
}

/**
 * Renders the SolidJS CSS consent banner with accept and reject actions.
 *
 * @param props Banner callbacks for accept and reject actions.
 * @returns A consent banner element that hides itself after dismissal.
 */
export const ConsentBanner: Component<ConsentBannerProps> = (props) => {
  const [visible, setVisible] = createSignal(props.show !== false);
  const [personalization, setPersonalization] = createSignal(true);
  const [analytics, setAnalytics] = createSignal(true);
  const [marketing, setMarketing] = createSignal(false);
  const copy = createAnalyticsCopy();
  const consent = useAnalyticsConsent();
  const currentSettings = (): ConsentSettings => ({
    essential: true,
    analytics: analytics(),
    marketing: marketing(),
    personalization: personalization(),
    timestamp: Date.now(),
  });
  const rejectedSettings = (): ConsentSettings => ({
    essential: true,
    analytics: false,
    marketing: false,
    personalization: false,
    timestamp: Date.now(),
  });
  const accept = (settings: ConsentSettings): void => {
    consent?.updateConsent(settings);
    props.onAccept(settings);
    setVisible(false);
  };
  const reject = (): void => {
    const settings = rejectedSettings();
    consent?.updateConsent(settings);
    props.onReject(settings);
    setVisible(false);
  };

  return (
    <Show when={visible()}>
      <FadeIn>
        <div role="region" aria-label={copy.t("analytics.consent.regionLabel")}>
          <Card class={cx("gn-analytics-consent-banner", props.class)}>
            <CardContent class="gn-analytics-consent-banner__body">
              <p class="gn-analytics-consent__text">
                {copy.t("analytics.consent.message")}
              </p>
              <fieldset class="gn-analytics-consent__preferences">
                <legend class="gn-analytics-consent__legend">
                  {copy.t("analytics.consent.preferencesLegend")}
                </legend>
                <label class="gn-analytics-consent__option">
                  <input
                    type="checkbox"
                    checked={personalization()}
                    onChange={(event) =>
                      setPersonalization(event.currentTarget.checked)
                    }
                  />
                  <span>{copy.t("analytics.consent.personalization")}</span>
                </label>
                <label class="gn-analytics-consent__option">
                  <input
                    type="checkbox"
                    checked={analytics()}
                    onChange={(event) =>
                      setAnalytics(event.currentTarget.checked)
                    }
                  />
                  <span>{copy.t("analytics.consent.analyticsScope")}</span>
                </label>
                <label class="gn-analytics-consent__option">
                  <input
                    type="checkbox"
                    checked={marketing()}
                    onChange={(event) =>
                      setMarketing(event.currentTarget.checked)
                    }
                  />
                  <span>{copy.t("analytics.consent.marketing")}</span>
                </label>
              </fieldset>
              <div class="gn-analytics-consent__actions">
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => {
                    accept({
                      essential: true,
                      analytics: true,
                      marketing: true,
                      personalization: true,
                      timestamp: Date.now(),
                    });
                  }}
                >
                  {copy.t("analytics.consent.acceptAll")}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    accept(currentSettings());
                  }}
                >
                  {copy.t("analytics.consent.savePreferences")}
                </Button>
                <Button type="button" variant="secondary" onClick={reject}>
                  {copy.t("analytics.consent.reject")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </FadeIn>
    </Show>
  );
};
